import { useState, type ReactNode } from 'react'

import { FilterIcon } from '@hugeicons/react'
import clsx from 'clsx'
import { Drawer } from 'vaul'

import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { useBodyScrollLock } from '@/shared/hooks/useBodyScrollLock'
import { Button } from '@/shared/ui/Button'

import { ResourceAdvancedFilterPanel } from './ResourceAdvancedFilterPanel'
import { useMobileViewport } from './useMobileViewport'

import type { ResourceAdvancedFilterRenderArgs } from './types'

type ResourceAdvancedFilterProps<T extends Record<string, unknown>> = {
  title: string
  clearLabel: string
  applyLabel: string
  appliedFilters: T
  hasActiveFilters: boolean
  isDraftActive: (draft: T) => boolean
  onApplyDraft: (draft: T) => void
  onReset: () => void
  emptyDraft: T
  renderFields: (args: ResourceAdvancedFilterRenderArgs<T>) => ReactNode
  hidden?: boolean
  className?: string
  triggerAriaLabel?: string
}

/**
 * Filtro avançado canônico — botão ícone + popover (desktop) ou bottom sheet (mobile).
 * No mobile, alterações ficam em rascunho até "Aplicar".
 */
export const ResourceAdvancedFilter = <T extends Record<string, unknown>>({
  title,
  clearLabel,
  applyLabel,
  appliedFilters,
  hasActiveFilters,
  isDraftActive,
  onApplyDraft,
  onReset,
  emptyDraft,
  renderFields,
  hidden = false,
  className,
  triggerAriaLabel
}: ResourceAdvancedFilterProps<T>) => {
  const isMobile = useMobileViewport()
  const [open, setOpen] = useState(false)
  const [draftFilters, setDraftFilters] = useState(appliedFilters)

  useBodyScrollLock(isMobile && open)

  if (hidden) return null

  const handleOpenChange = (nextOpen: boolean) => {
    if (nextOpen && isMobile) {
      setDraftFilters(appliedFilters)
    }
    setOpen(nextOpen)
  }

  const handleReset = () => {
    onReset()
    setOpen(false)
  }

  const handleMobileReset = () => {
    setDraftFilters(emptyDraft)
  }

  const handleMobileApply = () => {
    onApplyDraft(draftFilters)
    setOpen(false)
  }

  const updateDraft = (patch: Partial<T>) => {
    setDraftFilters(prev => ({ ...prev, ...patch }))
  }

  const updateApplied = (patch: Partial<T>) => {
    onApplyDraft({ ...appliedFilters, ...patch })
  }

  const desktopPanel = (
    <ResourceAdvancedFilterPanel
      title={title}
      clearLabel={clearLabel}
      hasActiveFilters={hasActiveFilters}
      onReset={handleReset}
    >
      {renderFields({
        layout: 'desktop',
        filters: appliedFilters,
        updateFilters: updateApplied
      })}
    </ResourceAdvancedFilterPanel>
  )

  const trigger = (
    <Button
      type="button"
      variant={hasActiveFilters ? 'primary-ghost' : 'light'}
      size="md"
      className="shrink-0 !aspect-square !p-0 !w-[var(--btn-h-md)] [&_svg]:size-[18px]"
      leadingIcon={<FilterIcon variant="stroke" />}
      aria-label={triggerAriaLabel ?? title}
      aria-expanded={open}
    />
  )

  if (isMobile) {
    return (
      <div className={clsx('flex items-center', className)}>
        <Drawer.Root open={open} onOpenChange={handleOpenChange}>
          <Drawer.Trigger asChild>{trigger}</Drawer.Trigger>
          <Drawer.Portal>
            <Drawer.Overlay className="fixed inset-0 z-sheet-backdrop bg-black/55" />
            <Drawer.Content className="fixed bottom-0 left-0 right-0 z-sheet mx-auto flex h-[min(85dvh,720px)] w-full max-w-2xl flex-col overflow-hidden rounded-t-2xl bg-white focus:outline-none">
              <div className="shrink-0 px-4 pt-3 pb-2">
                <Drawer.Handle className="mx-auto h-1 w-10 shrink-0 rounded-full bg-gray-200" />
              </div>

              <Drawer.Title className="shrink-0 px-4 pb-3 text-base font-bold leading-tight text-gray-900">
                {title}
              </Drawer.Title>

              <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto overscroll-contain px-4 pb-4">
                {renderFields({
                  layout: 'mobile',
                  filters: draftFilters,
                  updateFilters: updateDraft
                })}
              </div>

              <div className="shrink-0 border-t border-gray-200 bg-white px-4 pt-3 pb-[max(1rem,env(safe-area-inset-bottom))]">
                <div className="flex flex-col gap-2">
                  <Button
                    type="button"
                    variant="primary"
                    size="md"
                    className="w-full"
                    onClick={handleMobileApply}
                    showTrailingIcon={false}
                  >
                    {applyLabel}
                  </Button>
                  {isDraftActive(draftFilters) && (
                    <Button
                      type="button"
                      variant="light"
                      size="md"
                      className="w-full"
                      onClick={handleMobileReset}
                      showTrailingIcon={false}
                    >
                      {clearLabel}
                    </Button>
                  )}
                </div>
              </div>
            </Drawer.Content>
          </Drawer.Portal>
        </Drawer.Root>
      </div>
    )
  }

  return (
    <div className={clsx('flex items-center', className)}>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>{trigger}</PopoverTrigger>
        <PopoverContent
          align="end"
          sideOffset={8}
          className="z-popover w-[min(100vw-1.5rem,18rem)] rounded-2xl border-contour p-4 shadow-[0_8px_24px_rgba(76,29,149,0.12)]"
        >
          {desktopPanel}
        </PopoverContent>
      </Popover>
    </div>
  )
}

ResourceAdvancedFilter.displayName = 'ResourceAdvancedFilter'
